import _ from 'underscore';
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { MessageBar } from '@fluentui/react';
import { BrandListsPicker } from '@bingads-webui-campaign-react/brand-lists-components';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';

export const getBrandListsTitleDetails = (i18n, brandProtections) => {
  const { selectedBrandLists } = brandProtections || {};
  if (_.isEmpty(selectedBrandLists)) {
    return i18n.getString(_TL_('Add brand protections so that your ads will show on search queries that mention those brands.'));
  }
  if (selectedBrandLists.length === 1) {
    return i18n.getString(_TL_('You’ve added 1 brand list, which makes it so that your ads will show on search queries that mention those brands.'));
  }
  return i18n.getString(_TL_('You’ve added {{number}} brand lists, which makes it so that your ads will show on search queries that mention those brands.'), { number: selectedBrandLists.length });
};

const getStyles = (unused, { palette }) => ({
  brandListsPicker: {
    '&.disabled': {
      '& p,div': {
        color: palette.neutralTertiaryAlt,
      },
    },
  },
  messageBar: {
    background: palette.themeLighterAlt,
    marginBottom: 8,
  },
});

export const BrandLists = ({
  campaignId, updateValidation, disabled,
}) => {
  const classes = useDefaultStyles(getStyles);

  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext);
  const { aid } = scope.values;

  const {
    CurrentAccount: {
      // This is to handle agency cases
      AdvertiserCustomerID: cid,
    },
  } = serverContext;

  const onBrandListsChange = useCallback((selectedBrandLists, [deletedBrandListIds, addedBrandListIds]) => {
    updateValidation([], {
      brandProtections: {
        deletedBrandListIds,
        addedBrandListIds,
        selectedBrandLists,
      },
    });

    if (activity) {
      activity.signal(`onBrandListsChange - ${selectedBrandLists}`);
    }
  }, [updateValidation, activity]);

  return (
    <div className={classes.brandListsSection}>
      {
        disabled && (
          <MessageBar className={classes.messageBar}>
            {i18n.getString(_TL_('Please select predictive matching to enable brand protection.'))}
          </MessageBar>
        )
      }
      <div className={classNames(classes.brandListsPicker, disabled ? 'disabled' : '')}>
        {
          campaignId && (
            <BrandListsPicker
              className="search-campaign-brand-lists-picker"
              cid={cid}
              aid={aid}
              campaignId={campaignId}
              onChange={onBrandListsChange}
              isConfirmDialogOn
              descriptionTextTemplate={i18n.getString(_TL_('Add brand protections so that your ads will show on search queries that mention those brands. You can add up to {{count}} brand lists to your Search campaign. To manage your brand lists, go to {{link}}Brand lists{{/link}}.'))}
              noInitialDataText={i18n.getString(_TL_('Create a brand list so that your ads serve on search queries that mention those brands.'))}
              disabled={disabled}
            />
          )
        }
      </div>
    </div>);
};

BrandLists.propTypes = {
  campaignId: PropTypes.number.isRequired,
  updateValidation: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

BrandLists.defaultProps = {
  disabled: false,
};

export const ValidatedBrandLists = withError(withCustomValidation(BrandLists));
